import { Flex, Text } from "@chakra-ui/react";
import React, { FC, useMemo } from "react";

import type { CustomInsightsInterviewPreviewQuery } from "../../../graphql";

type Interview =
  CustomInsightsInterviewPreviewQuery["customInsightsInterviewPreview"]["interviews"][number];

type DataAvailabilityIndicatorProps = {
  interviews: Interview[];
  isLoading: boolean;
  label?: string;
};

type MetricStat = {
  label: string;
  percent: number;
};

function getColor(percent: number): string {
  if (percent >= 90) return "green.600";
  if (percent >= 70) return "yellow.700";
  return "red.600";
}

export const DataAvailabilityIndicator: FC<DataAvailabilityIndicatorProps> = ({
  interviews,
  isLoading,
  label = "Data availability:",
}) => {
  const stats = useMemo((): MetricStat[] | null => {
    const total = interviews.length;
    if (total === 0) return null;
    return [
      {
        label: "Position",
        percent: Math.round(
          (interviews.filter((i) => i.hasPosition).length / total) * 100
        ),
      },
      {
        label: "AI notes",
        percent: Math.round(
          (interviews.filter((i) => i.hasAiNotes).length / total) * 100
        ),
      },
      {
        label: "Transcripts",
        percent: 100,
      },
      {
        label: "Scorecards",
        percent: Math.round(
          (interviews.filter((i) => i.hasScorecard).length / total) * 100
        ),
      },
    ];
  }, [interviews]);

  if (isLoading || !stats) return null;

  return (
    <Flex align="center" gap="8px" flexWrap="nowrap" whiteSpace="nowrap">
      <Text
        fontSize="12px"
        lineHeight="16px"
        fontWeight="semibold"
        color="gray.600"
      >
        {label}
      </Text>
      <Flex gap="12px" align="center">
        {stats.map((stat) => (
          <Flex key={stat.label} gap="4px" align="center">
            <Text
              fontSize="12px"
              lineHeight="16px"
              color={getColor(stat.percent)}
            >
              {stat.label}
            </Text>
            <Text
              fontSize="12px"
              lineHeight="16px"
              color={getColor(stat.percent)}
            >
              {stat.percent}%
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
